import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

export default function AddressAutocomplete({ value, onChange, onPick, label = "Address" }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const timerRef = useRef(null);
    const justPickedRef = useRef(false);
    const userTypedRef = useRef(false);
    const dirtyRef = useRef(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (justPickedRef.current) { justPickedRef.current = false; return; }
        // Skip auto-geocode for prefilled values — only fire once the user actually types.
        if (!userTypedRef.current) return;
        if (!value || value.length < 3) { setSuggestions([]); return; }
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            setBusy(true);
            try {
                const { data } = await api.get(`/geocode?q=${encodeURIComponent(value)}`);
                setSuggestions(data);
                setOpen(true);
            } catch { setSuggestions([]); }
            finally { setBusy(false); }
        }, 500);
        return () => clearTimeout(timerRef.current);
    }, [value]);

    const resolveTyped = async () => {
        setOpen(false);
        if (!dirtyRef.current || !value || value.trim().length < 3) return;
        try {
            const { data } = await api.get(`/geocode?q=${encodeURIComponent(value)}`);
            if (data.length) pick({ ...data[0], display_name: value });
            else setNotFound(true);
        } catch {}
    };

    const pick = (s) => {
        dirtyRef.current = false;
        setNotFound(false);
        justPickedRef.current = true;
        onPick(s);
        setOpen(false);
        setSuggestions([]);
    };

    return (
        <div className="relative">
            <Label>{label}</Label>
            <div className="relative">
                <Input
                    data-testid="address-input"
                    value={value}
                    onChange={(e) => { userTypedRef.current = true; dirtyRef.current = true; setNotFound(false); onChange(e.target.value); }}
                    onFocus={() => suggestions.length && setOpen(true)}
                    onBlur={() => setTimeout(resolveTyped, 200)}
                    placeholder="Start typing an address…"
                />
                {busy && <Loader2 size={14} className="absolute right-3 top-3 animate-spin text-muted-foreground" />}
            </div>
            {notFound && (
                <p data-testid="address-not-found" className="text-xs text-destructive mt-1">
                    Couldn't find that address — try just the postcode (e.g. G64 2AL).
                </p>
            )}
            {open && suggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 w-full bg-card border border-stone-200 rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto">
                    {suggestions.map((s, i) => (
                        <li key={i}>
                            <button
                                type="button"
                                data-testid={`address-suggestion-${i}`}
                                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                                className="w-full text-left px-4 py-2.5 hover:bg-secondary flex items-start gap-2 text-sm"
                            >
                                <MapPin size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                                <span>{s.display_name}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
